/**
 * useAzione — la pagina apre il suo modulo quando ci si arriva da
 * un'azione della barra comandi (Ctrl K) o del pulsante «Nuovo».
 *
 * Le azioni stanno nella tabella delle schermate (navigazione/schermate.js,
 * il campo `azioni`) e si aprono andando al percorso della schermata con lo
 * stato { azione: codice } (apriDestinazione, qui sotto). La pagina dice che
 * cosa fa per ogni codice:
 *
 *   useAzione({ 'nuovo-compito': () => setScheda('nuovo') });
 *
 * e il gestore e' LO STESSO gesto del pulsante della pagina, non una
 * seconda strada: l'azione della barra non puo' fare niente che il
 * pulsante non faccia.
 *
 * L'AZIONE SI CONSUMA. Appena letta, si toglie dallo stato della voce
 * della cronologia (sostituendola): «Indietro» o un ricaricamento della
 * pagina non riaprono il modulo, che e' quello che chi l'ha chiuso si
 * aspetta. Si ascolta la CHIAVE della posizione, non il montaggio: chi e'
 * gia' sulla pagina e sceglie la stessa azione dalla barra arriva con una
 * posizione nuova e la pagina, che resta montata, la vede lo stesso.
 *
 * E IL MODULO SI APRE DOPO, non insieme alla sostituzione. Le finestre che
 * «Indietro» chiude (utils/finestraIndietro.js, la fattura nuova) mettono un
 * passo nella cronologia quando si aprono, e guardano la posizione per
 * capire se qualcuno e' tornato indietro: aperta nello stesso giro della
 * sostituzione, la finestra vedrebbe cambiare la posizione sotto di se' e
 * si richiuderebbe da sola. Quindi prima si sostituisce, e al giro dopo -
 * posizione nuova, senza azione - si chiama il gestore.
 *
 * QUELLO CHE ARRIVA CON L'AZIONE SI CONSUMA CON LEI (revisione del
 * 6/10/2026). «Appuntamento» dalla cartella porta accanto all'azione il
 * paziente gia' scelto (cognome, nome, telefono, nascita): fino a quel giorno
 * si toglieva l'azione e il resto restava nello stato della voce della
 * cronologia, anche dopo una ricarica - un dato del paziente lasciato li'
 * senza che servisse piu'. Adesso la voce sostituita non porta piu' niente,
 * e il gestore riceve il resto come secondo argomento:
 *
 *   useAzione({ 'nuovo-appuntamento': (azione, dati) => apri(dati.paziente) });
 */
import { useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { confermaUscita } from '../utils/uscitaProtetta';

export default function useAzione(gestori) {
    const location = useLocation();
    const navigate = useNavigate();
    const attuali = useRef(gestori);
    attuali.current = gestori;
    /* La stessa posizione non si consuma due volte (in sviluppo React
       ripete gli effetti al montaggio). */
    const consumata = useRef(null);
    /* L'azione letta e tolta, coi dati che portava, che aspetta il giro
       dopo la sostituzione: { azione, dati }. */
    const inAttesa = useRef(null);

    useEffect(() => {
        const stato = location.state;
        const azione = stato && stato.azione;
        if (azione && consumata.current !== location.key && typeof attuali.current[azione] === 'function') {
            consumata.current = location.key;
            const dati = { ...stato };
            delete dati.azione;
            inAttesa.current = { azione, dati };
            navigate(`${location.pathname}${location.search}${location.hash}`, { replace: true, state: null });
            return;
        }
        if (inAttesa.current && !azione) {
            const { azione: daFare, dati } = inAttesa.current;
            inAttesa.current = null;
            const gestore = attuali.current[daFare];
            if (typeof gestore === 'function') gestore(daFare, dati);
        }
    }, [location.key, location.state, location.pathname, location.search, location.hash, navigate]);
}

/**
 * Apre una destinazione della barra comandi (o di «Nuovo»): una schermata,
 * un'azione, un paziente. UNA regola per tutti e due, perche' sulla STESSA
 * pagina il router non chiede niente:
 *   - un'altra pagina: navigate, e se c'e' un modulo non salvato chiede
 *     GuardiaUscita (utils/uscitaProtetta.js), come per una voce del menu;
 *   - la pagina dove si e' gia', senza azione: non si fa niente (una voce
 *     doppia nella cronologia farebbe girare «Indietro» a vuoto);
 *   - la pagina dove si e' gia', con un'azione: il router non la vede come
 *     un'uscita, quindi si chiede qui con la stessa domanda
 *     (confermaUscita), e si sostituisce la voce invece di aggiungerne una.
 *     LA VOCE SOSTITUITA TIENE RICERCA E ANCORA dell'indirizzo: la pagina e'
 *     la stessa, e quello che dice l'indirizzo e' suo. Revisione dell'unione
 *     beta-unico (3/10/2026): l'agenda tiene il giorno in ?giorno= (ramo
 *     ricarica), e «Nuovo appuntamento» da Ctrl K o da «Nuovo» sostituiva la
 *     voce col solo percorso: l'agenda tornava su oggi mentre l'avviso
 *     diceva «fai clic sull'orario» (scenario 15 dei percorsi, caso 15b).
 * `attuale` e' la posizione del router (useLocation): pathname, search, hash.
 * Torna 'aperto', 'gia-qui' o 'fermato'.
 */
export function apriDestinazione(navigate, attuale, { percorso, stato }, conferma = confermaUscita) {
    const stessa = percorso === attuale.pathname;
    if (stessa && !stato) return 'gia-qui';
    if (stessa && !conferma()) return 'fermato';
    if (stessa) {
        navigate({ pathname: percorso, search: attuale.search || '', hash: attuale.hash || '' },
            { state: stato, replace: true });
        return 'aperto';
    }
    navigate(percorso, stato ? { state: stato } : {});
    return 'aperto';
}
